import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
  Drawer,
  Badge,
  Tooltip,
  Typography,
} from '@mui/material';
import { FaBars, FaRegCalendarAlt, FaTimes, FaSearch } from 'react-icons/fa';
import logo from '../assets/images/logos/logo.png';
import useTripList from '../hooks/useTripList';
import { BRAND } from '../theme';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'Stadiums', path: '/stadiums' },
  { title: 'Trip planner', path: '/planner' },
  { title: 'World Cup 2026', path: '/world-cup-2026' },
];

const isActivePath = (location, path) => {
  if (path === '/') return location.pathname === '/';
  return location.pathname.startsWith(path);
};

const Navbar = () => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // The badge counts PLANNED TRIPS, not bookmarked grounds. The heart it
  // replaces (W5b) counted the retired saved-stadiums key, which nothing writes
  // any more. `useTripList` carries the cross-tab subscription, so deleting a
  // trip in one tab decrements the count in the other.
  const { trips } = useTripList();
  const savedCount = trips.length;

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: scrolled
          ? 'rgba(255, 255, 255, 0.92)'
          : 'rgba(255, 255, 255, 0.98)',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: '1px solid',
        borderColor: scrolled
          ? 'rgba(20, 33, 61, 0.1)'
          : 'rgba(20, 33, 61, 0.05)',
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            minHeight: {
              xs: 56,
              md: scrolled ? 56 : 72,
            },
            transition: 'min-height 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              mr: 'auto',
            }}
          >
            <img
              src={logo}
              alt="Your Away Day"
              style={{
                height: scrolled ? 44 : 52,
                width: 'auto',
                transition: 'height 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 0.5,
              alignItems: 'center',
            }}
          >
            {pages.map((page) => {
              const active = isActivePath(location, page.path);
              return (
                <Button
                  key={page.title}
                  component={Link}
                  to={page.path}
                  sx={{
                    color: active ? 'text.primary' : 'text.secondary',
                    fontWeight: active ? 600 : 400,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    position: 'relative',
                    '&::after': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: 16,
                          right: 16,
                          height: 2,
                          borderRadius: 1,
                          bgcolor: 'primary.main',
                        }
                      : {},
                    '&:hover': {
                      bgcolor: 'rgba(20, 33, 61, 0.04)',
                    },
                  }}
                >
                  {page.title}
                </Button>
              );
            })}

            <Box
              sx={{
                width: '1px',
                height: 24,
                bgcolor: 'divider',
                mx: 1,
              }}
            />

            <Tooltip title="Search stadiums">
              <IconButton
                component={Link}
                to="/search"
                aria-label="Search stadiums"
                sx={{
                  color:
                    location.pathname === '/search'
                      ? 'primary.main'
                      : 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <FaSearch size={15} />
              </IconButton>
            </Tooltip>

            <Tooltip title="My trips">
              <IconButton
                component={Link}
                to="/my-trips"
                aria-label={`My trips${savedCount > 0 ? ` (${savedCount})` : ''}`}
                sx={{
                  color:
                    location.pathname === '/my-trips'
                      ? 'primary.main'
                      : 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Badge
                  badgeContent={savedCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.625rem',
                      minWidth: 18,
                      height: 18,
                    },
                  }}
                >
                  <FaRegCalendarAlt size={16} />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mobile: my trips + hamburger */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <IconButton
              component={Link}
              to="/search"
              size="small"
              aria-label="Search stadiums"
              sx={{
                color:
                  location.pathname === '/search'
                    ? 'primary.main'
                    : 'text.secondary',
              }}
            >
              <FaSearch size={14} />
            </IconButton>
            <IconButton
              component={Link}
              to="/my-trips"
              size="small"
              aria-label={`My trips${savedCount > 0 ? ` (${savedCount})` : ''}`}
              sx={{ color: 'text.secondary' }}
            >
              <Badge badgeContent={savedCount} color="primary">
                <FaRegCalendarAlt size={14} />
              </Badge>
            </IconButton>
            <IconButton
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ color: 'text.primary' }}
            >
              <FaBars size={18} />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.default',
            pt: 2,
          },
        }}
      >
        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <FaTimes size={18} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3 }}>
          {pages.map((page) => {
            const active = isActivePath(location, page.path);
            return (
              <Box
                key={page.title}
                component={Link}
                to={page.path}
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: active ? 'primary.main' : 'text.primary',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {page.title}
                </Typography>
              </Box>
            );
          })}
          <Box
            component={Link}
            to="/search"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              textDecoration: 'none',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FaSearch
              size={14}
              style={{
                color:
                  location.pathname === '/search' ? BRAND.red : BRAND.textSecondary,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color:
                  location.pathname === '/search'
                    ? 'primary.main'
                    : 'text.primary',
                fontWeight: location.pathname === '/search' ? 600 : 400,
              }}
            >
              Search
            </Typography>
          </Box>
          <Box
            component={Link}
            to="/my-trips"
            sx={{
              display: 'block',
              textDecoration: 'none',
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color:
                  location.pathname === '/my-trips'
                    ? 'primary.main'
                    : 'text.primary',
                fontWeight:
                  location.pathname === '/my-trips' ? 600 : 400,
              }}
            >
              My Trips{savedCount > 0 ? ` (${savedCount})` : ''}
            </Typography>
          </Box>
          <Box
            component={Link}
            to="/advertise"
            sx={{
              display: 'block',
              textDecoration: 'none',
              py: 1.5,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color:
                  location.pathname === '/advertise'
                    ? 'primary.main'
                    : 'text.primary',
                fontWeight:
                  location.pathname === '/advertise' ? 600 : 400,
              }}
            >
              Advertise
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
